import { useEffect, useMemo, useRef, useState, type ChangeEvent, type FormEvent } from "react";
import Modal from "@/components/base/Modal";
import { Field, Input, Select, Textarea } from "@/components/base/FormControls";
import { useTerritory } from "@/store/territory/TerritoryProvider";
import { useImmeubles } from "../ImmeublesProvider";
import type { Immeuble, ImmeubleInput } from "../types";

interface ImmeubleFormProps {
  open: boolean;
  onClose: () => void;
  initial?: Immeuble;
}

interface FormState {
  nom: string;
  description: string;
  photo: string;
  regionId: string;
  provinceId: string;
  communeId: string;
  secteurId: string;
  adresse: string;
  niveaux: string;
  infosComplementaires: string;
}

const EMPTY_STATE: FormState = {
  nom: "",
  description: "",
  photo: "",
  regionId: "",
  provinceId: "",
  communeId: "",
  secteurId: "",
  adresse: "",
  niveaux: "1",
  infosComplementaires: "",
};

export default function ImmeubleForm({ open, onClose, initial }: ImmeubleFormProps) {
  const { addImmeuble, updateImmeuble } = useImmeubles();
  const {
    regions,
    provinces,
    communes,
    secteurs,
    provincesOf,
    communesOf,
    secteurOf,
    regionName,
    provinceName,
    communeName,
    secteurName,
    loading,
    error,
    reload,
  } = useTerritory();
  const [form, setForm] = useState<FormState>(EMPTY_STATE);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const fileRef = useRef<HTMLInputElement>(null);

  const regionOptions = useMemo(
    () => regions.map((region) => ({ value: region.id, label: region.nom })),
    [regions]
  );
  const provinceOptions = useMemo(
    () => provincesOf(form.regionId).map((province) => ({ value: province.id, label: province.nom })),
    [provincesOf, form.regionId]
  );
  const communeOptions = useMemo(
    () => communesOf(form.provinceId).map((commune) => ({ value: commune.id, label: commune.nom })),
    [communesOf, form.provinceId]
  );
  const secteurOptions = useMemo(
    () => secteurOf(form.communeId).map((secteur) => ({ value: secteur.id, label: secteur.nom })),
    [secteurOf, form.communeId]
  );

  useEffect(() => {
    if (!open) return;
    if (initial) {
      // On privilégie les UUID ; repli sur le libellé pour d'anciens enregistrements.
      const regionId =
        initial.regionId || regions.find((item) => item.nom === initial.region)?.id || "";
      const provinceId =
        initial.provinceId ||
        provinces.find((item) => item.nom === initial.province && (!regionId || item.regionId === regionId))?.id ||
        "";
      const communeId =
        initial.communeId ||
        communes.find((item) => item.nom === initial.commune && (!provinceId || item.provinceId === provinceId))?.id ||
        "";
      const secteurId =
        initial.secteurId ||
        secteurs.find((item) => item.nom === initial.secteur && (!communeId || item.communeId === communeId))?.id ||
        "";
      setForm({
        nom: initial.nom,
        description: initial.description,
        photo: initial.photo,
        regionId,
        provinceId,
        communeId,
        secteurId,
        adresse: initial.adresse,
        niveaux: String(initial.niveaux),
        infosComplementaires: initial.infosComplementaires,
      });
    } else {
      setForm(EMPTY_STATE);
    }
    setErrors({});
  }, [open, initial]);

  const update = (key: keyof FormState, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleRegion = (value: string) => {
    setForm((prev) => ({ ...prev, regionId: value, provinceId: "", communeId: "", secteurId: "" }));
  };

  const handleProvince = (value: string) => {
    setForm((prev) => ({ ...prev, provinceId: value, communeId: "", secteurId: "" }));
  };

  const handleCommune = (value: string) => {
    setForm((prev) => ({ ...prev, communeId: value, secteurId: "" }));
  };

  const handleFile = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setForm((prev) => ({ ...prev, photo: URL.createObjectURL(file) }));
    }
  };

  const resetAndClose = () => {
    setForm(EMPTY_STATE);
    setErrors({});
    onClose();
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const nextErrors: Record<string, string> = {};
    if (!form.nom.trim()) nextErrors.nom = "Le nom de l'immeuble est obligatoire.";
    if (!form.regionId) nextErrors.regionId = "Sélectionnez une région.";
    if (!form.provinceId) nextErrors.provinceId = "Sélectionnez une province.";
    if (!form.communeId) nextErrors.communeId = "Sélectionnez une commune ou une ville.";

    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    const input: ImmeubleInput = {
      nom: form.nom.trim(),
      description: form.description.trim(),
      photo: form.photo,
      regionId: form.regionId,
      provinceId: form.provinceId,
      communeId: form.communeId,
      secteurId: form.secteurId,
      region: regionName(form.regionId),
      province: provinceName(form.provinceId),
      commune: communeName(form.communeId),
      secteur: secteurName(form.secteurId),
      adresse: form.adresse.trim(),
      niveaux: Math.max(1, Number(form.niveaux) || 1),
      infosComplementaires: form.infosComplementaires.trim(),
    };

    if (initial) {
      updateImmeuble(initial.id, input);
    } else {
      addImmeuble(input);
    }
    resetAndClose();
  };

  const provincePlaceholder = !form.regionId
    ? "Choisissez d'abord une région"
    : provinceOptions.length > 0
      ? "Sélectionnez une province"
      : "Aucune province disponible";
  const communePlaceholder = !form.provinceId
    ? "Choisissez d'abord une province"
    : communeOptions.length > 0
      ? "Sélectionnez une commune"
      : "Aucune commune disponible";
  const secteurPlaceholder = !form.communeId
    ? "Choisissez d'abord une commune"
    : secteurOptions.length > 0
      ? "Sélectionnez un secteur / quartier"
      : "Aucun secteur disponible";

  return (
    <Modal
      open={open}
      onClose={resetAndClose}
      size="lg"
      title={initial ? "Modifier l'immeuble" : "Ajouter un immeuble"}
      description="Renseignez les informations de votre immeuble. Seuls le nom et la localisation sont obligatoires."
      footer={
        <>
          <button
            type="button"
            onClick={resetAndClose}
            className="inline-flex items-center justify-center whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-5 py-2.5 text-sm font-label font-medium text-foreground-900 transition-colors hover:bg-background-100 cursor-pointer"
          >
            Annuler
          </button>
          <button
            type="submit"
            form="immeuble-form"
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
          >
            <i className="ri-check-line text-base" aria-hidden="true" />
            Enregistrer l'immeuble
          </button>
        </>
      }
    >
      <form id="immeuble-form" onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
        {error && (
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-background-100 px-3.5 py-2.5 text-xs text-foreground-700">
            <span className="flex items-center gap-1.5">
              <i className="ri-error-warning-line text-base text-accent-600" aria-hidden="true" />
              Impossible de charger les territoires. {error}
            </span>
            <button
              type="button"
              onClick={reload}
              className="inline-flex items-center gap-1 whitespace-nowrap font-label font-medium text-primary-700 transition-colors hover:text-primary-800 cursor-pointer"
            >
              <i className="ri-refresh-line text-sm" aria-hidden="true" />
              Réessayer
            </button>
          </div>
        )}
        {loading && !error && (
          <p className="text-xs text-foreground-500">Chargement des territoires…</p>
        )}

        <div className="grid grid-cols-1 gap-x-4 gap-y-3.5 sm:grid-cols-2">
          <Field
            label="Nom de l'immeuble"
            htmlFor="immeuble-nom"
            required
            error={errors.nom}
            className="md:col-span-2"
          >
            <Input
              id="immeuble-nom"
              value={form.nom}
              onChange={(event) => update("nom", event.target.value)}
              placeholder="Ex. Résidence Laafi, Immeuble Wend-Kuni"
              invalid={Boolean(errors.nom)}
              maxLength={120}
            />
          </Field>

          <Field label="Description" htmlFor="immeuble-description" className="md:col-span-2">
            <Textarea
              id="immeuble-description"
              value={form.description}
              onChange={(event) => update("description", event.target.value)}
              placeholder="Décrivez brièvement l'immeuble, son usage, ses atouts..."
              className="min-h-[96px]"
              maxLength={600}
            />
          </Field>

          <Field label="Photo" htmlFor="immeuble-photo" hint="Aperçu local (frontend). L'envoi réel viendra avec le stockage." className="md:col-span-2">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <div className="h-24 w-full overflow-hidden rounded-md border border-background-200 bg-background-100 sm:w-40">
                {form.photo ? (
                  <img src={form.photo} alt="Aperçu de l'immeuble" className="h-24 w-full object-cover object-top" />
                ) : (
                  <div className="flex h-24 w-full items-center justify-center text-foreground-300">
                    <i className="ri-image-add-line text-3xl" aria-hidden="true" />
                  </div>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-3.5 py-2 text-sm font-label font-medium text-foreground-900 transition-colors hover:bg-background-100 cursor-pointer"
                >
                  <i className="ri-upload-2-line text-base" aria-hidden="true" />
                  Choisir une image
                </button>
                {form.photo && (
                  <button
                    type="button"
                    onClick={() => update("photo", "")}
                    className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md px-3 py-2 text-sm font-label font-medium text-primary-700 transition-colors hover:bg-primary-50 cursor-pointer"
                  >
                    <i className="ri-delete-bin-line text-base" aria-hidden="true" />
                    Retirer
                  </button>
                )}
              </div>
              <input
                ref={fileRef}
                id="immeuble-photo"
                type="file"
                accept="image/*"
                onChange={handleFile}
                className="hidden"
              />
            </div>
          </Field>

          <Field label="Région" htmlFor="immeuble-region" required error={errors.regionId}>
            <Select
              id="immeuble-region"
              value={form.regionId}
              onChange={(event) => handleRegion(event.target.value)}
              options={regionOptions}
              placeholder="Sélectionnez une région"
              disabled={loading || regionOptions.length === 0}
              invalid={Boolean(errors.regionId)}
            />
          </Field>

          <Field label="Province" htmlFor="immeuble-province" required error={errors.provinceId}>
            <Select
              id="immeuble-province"
              value={form.provinceId}
              onChange={(event) => handleProvince(event.target.value)}
              options={provinceOptions}
              placeholder={provincePlaceholder}
              disabled={!form.regionId || provinceOptions.length === 0}
              invalid={Boolean(errors.provinceId)}
            />
          </Field>

          <Field label="Commune / Ville" htmlFor="immeuble-commune" required error={errors.communeId}>
            <Select
              id="immeuble-commune"
              value={form.communeId}
              onChange={(event) => handleCommune(event.target.value)}
              options={communeOptions}
              placeholder={communePlaceholder}
              disabled={!form.provinceId || communeOptions.length === 0}
              invalid={Boolean(errors.communeId)}
            />
          </Field>

          <Field label="Secteur / Quartier" htmlFor="immeuble-secteur">
            <Select
              id="immeuble-secteur"
              value={form.secteurId}
              onChange={(event) => update("secteurId", event.target.value)}
              options={secteurOptions}
              placeholder={secteurPlaceholder}
              disabled={!form.communeId || secteurOptions.length === 0}
            />
          </Field>

          <Field label="Adresse" htmlFor="immeuble-adresse" className="md:col-span-2">
            <Input
              id="immeuble-adresse"
              value={form.adresse}
              onChange={(event) => update("adresse", event.target.value)}
              placeholder="Ex. Avenue Kwame N'Krumah, face à la pharmacie"
              maxLength={160}
            />
          </Field>

          <Field label="Nombre de niveaux" htmlFor="immeuble-niveaux" hint="Entrez 1 pour un bâtiment de plein pied.">
            <Input
              id="immeuble-niveaux"
              type="number"
              min={1}
              max={50}
              value={form.niveaux}
              onChange={(event) => update("niveaux", event.target.value)}
            />
          </Field>

          <Field label="Informations complémentaires" htmlFor="immeuble-infos" className="md:col-span-2">
            <Textarea
              id="immeuble-infos"
              value={form.infosComplementaires}
              onChange={(event) => update("infosComplementaires", event.target.value)}
              placeholder="Ex. cour commune, forage, groupe électrogène, gardien, compteurs individuels..."
              maxLength={600}
            />
          </Field>
        </div>

      </form>
    </Modal>
  );
}